import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from '../utils/analytics';
import { dashaCardStateFrom } from '../utils/dashaCardState';
import { 
  Star, 
  Calendar, 
  Heart, 
  MessageCircle,
  Eye,
  Users,
  Download,
  Sparkles,
  Activity,
  Sun,
  Moon,
  ArrowRight,
  ChevronDown,
  User,
  Lock,
  Globe,
  Timer,
  Clock,
  Plus,
  Calculator,
  Flame,
  Wallet,
  Grid,
  History,
  BarChart3,
  Telescope,
  ChevronRight,
} from 'lucide-react';

// ── Hora Chakra Wheel — overflow-safe, matches app animation ────────────────
const HoraChakra = ({ planet, symbol, timeRange, size = 144, onClick }) => {
  const colorSchemes = {
    Sun:     { bg: 'rgba(120,53,15,0.85)',  border: '#F59E0B', glow: 'rgba(245,158,11,0.4)',  text: '#FCD34D' },
    Moon:    { bg: 'rgba(30,41,59,0.85)',   border: '#93C5FD', glow: 'rgba(147,197,253,0.35)', text: '#BFDBFE' },
    Mars:    { bg: 'rgba(127,29,29,0.85)',  border: '#F87171', glow: 'rgba(248,113,113,0.4)', text: '#FCA5A5' },
    Mercury: { bg: 'rgba(6,78,59,0.85)',    border: '#34D399', glow: 'rgba(52,211,153,0.35)', text: '#6EE7B7' },
    Jupiter: { bg: 'rgba(120,53,15,0.85)',  border: '#FBBF24', glow: 'rgba(251,191,36,0.4)',  text: '#FDE68A' },
    Venus:   { bg: 'rgba(131,24,67,0.85)',  border: '#F9A8D4', glow: 'rgba(249,168,212,0.35)',text: '#FBCFE8' },
    Saturn:  { bg: 'rgba(49,46,129,0.85)',  border: '#818CF8', glow: 'rgba(129,140,248,0.35)',text: '#A5B4FC' },
  };
  const c = colorSchemes[planet] || colorSchemes.Sun;
  const r = size / 2;
  const r1 = r - 4;
  const r2 = r * 0.72;
  const r3 = r * 0.52;
  const outerDur = '20s';
  const midDur   = '12s';
  const innerDur = '8s';

  const containerRef = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(true);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.05 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <button
      ref={containerRef}
      onClick={onClick}
      style={{
        position: 'relative', width: size, height: size,
        borderRadius: '50%', border: 'none',
        background: 'none', cursor: 'pointer', padding: 0,
        flexShrink: 0,
        boxShadow: `0 0 40px ${c.glow}, 0 0 80px ${c.glow}33`,
      }}
    >
      <svg
        width={size} height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden' }}
      >
        <defs>
          <style>{`
            @keyframes hc-cw  { from { transform-origin: ${r}px ${r}px; transform: rotate(0deg);    } to { transform-origin: ${r}px ${r}px; transform: rotate(360deg);  } }
            @keyframes hc-ccw { from { transform-origin: ${r}px ${r}px; transform: rotate(0deg);    } to { transform-origin: ${r}px ${r}px; transform: rotate(-360deg); } }
            @keyframes hc-pulse { 0%,100%{opacity:0.5;}50%{opacity:0.9;} }
            .hc-l1 { animation: hc-cw  ${outerDur} linear infinite; }
            .hc-l2 { animation: hc-cw  ${midDur}   linear infinite; }
            .hc-l3 { animation: hc-ccw ${innerDur}  linear infinite; }
            .hc-bg-pulse { animation: hc-pulse 3s ease-in-out infinite; }
            @media (prefers-reduced-motion: reduce) {
              .hc-l1, .hc-l2, .hc-l3, .hc-bg-pulse {
                animation: none !important;
              }
            }
          `}</style>
          <clipPath id={`hc-clip-${size}`}>
            <circle cx={r} cy={r} r={r} />
          </clipPath>
          <radialGradient id={`hc-grad-${size}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={c.border} stopOpacity="0.25" />
            <stop offset="100%" stopColor={c.bg.replace('rgba','rgb').replace(/,[\d.]+\)/,')')} stopOpacity="0" />
          </radialGradient>
        </defs>
        <g clipPath={`url(#hc-clip-${size})`}>
          <circle cx={r} cy={r} r={r} fill={c.bg} />
          <circle cx={r} cy={r} r={r} fill={`url(#hc-grad-${size})`} className="hc-bg-pulse" style={{ animationPlayState: isVisible ? 'running' : 'paused' }} />
          <g className="hc-l1" style={{ animationPlayState: isVisible ? 'running' : 'paused' }}>
            {Array.from({length: 16}, (_, i) => {
              const a = (i * 360) / 16;
              const rad = a * Math.PI / 180;
              const cx = r + Math.cos(rad) * r1;
              const cy = r + Math.sin(rad) * r1;
              return (
                <ellipse key={i} cx={cx} cy={cy} rx={size * 0.06} ry={size * 0.03}
                  fill={`${c.border}30`} stroke={c.border} strokeWidth="0.6"
                  transform={`rotate(${a}, ${cx}, ${cy})`} opacity="0.75" />
              );
            })}
          </g>
          <g className="hc-l1" style={{ animation: 'hc-cw 60s linear infinite', animationPlayState: isVisible ? 'running' : 'paused' }}>
            {Array.from({length: 12}, (_, i) => {
              const a = i * 30;
              const rad = a * Math.PI / 180;
              const x1 = r + Math.cos(rad) * (r - 8);
              const y1 = r + Math.sin(rad) * (r - 8);
              const x2 = r + Math.cos(rad) * (r - 2);
              const y2 = r + Math.sin(rad) * (r - 2);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={c.border} strokeWidth={i % 3 === 0 ? 1.5 : 0.8} opacity="0.5" />;
            })}
          </g>
          <g className="hc-l2" style={{ animationPlayState: isVisible ? 'running' : 'paused' }}>
            {Array.from({length: 8}, (_, i) => {
              const a = i * 45 * Math.PI / 180;
              return <line key={i} x1={r} y1={r} x2={r + Math.cos(a) * r2} y2={r + Math.sin(a) * r2} stroke={c.border} strokeWidth="0.8" opacity="0.4" />;
            })}
            <circle cx={r} cy={r} r={r2} stroke={c.border} strokeWidth="0.7" fill="none" opacity="0.3" />
            <circle cx={r} cy={r} r={r3} stroke={c.border} strokeWidth="0.5" fill="none" opacity="0.25" />
          </g>
          <g className="hc-l3" style={{ animationPlayState: isVisible ? 'running' : 'paused' }}>
            <circle cx={r} cy={r} r={r3 * 0.65} stroke={c.border} strokeWidth="0.6" fill="none" opacity="0.4" />
          </g>
          <circle cx={r} cy={r} r={r - 2} stroke={c.border} strokeWidth="1.5" fill="none" opacity="0.7" />
        </g>
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        borderRadius: '50%', zIndex: 2,
      }}>
        <span style={{ fontSize: size * 0.22, lineHeight: 1, marginBottom: 2, opacity: 0.92 }}>{symbol || '☉'}</span>
        <span style={{ color: c.text, fontWeight: 800, fontSize: size * 0.12, letterSpacing: 1 }}>{(planet || 'SUN').toUpperCase()}</span>
        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: size * 0.07, letterSpacing: 2, marginTop: 2 }}>HORA LORD</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ADE80', animation: 'hc-pulse 2s ease-in-out infinite' }} />
          <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: size * 0.068, fontWeight: 600 }}>{timeRange || 'Active'}</span>
        </div>
      </div>
    </button>
  );
};

const DownloadButton = React.lazy(() => import(/* webpackChunkName: "reports" */ './DownloadButton'));
import LegalFooter from './LegalFooter';
const AstrologerDashboard = React.lazy(() => import(/* webpackChunkName: "astrologer" */ './AstrologerDashboard'));
const CosmicStore = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './CosmicStore'));
const SubscriptionManager = React.lazy(() => import(/* webpackChunkName: "store-billing" */ './SubscriptionManager'));
import CosmicSnapshot from './CosmicSnapshot';
import DashaTimelineCard from './DashaTimelineCard';
// CosmicInsightCard moved to EnhancedDivisionalCharts (D1 tab)
import ChitraguptCTACard from './ChitraguptCTACard';
import KarmaPulseCard from './KarmaPulseCard';
const MuhuratModal = React.lazy(() => import(/* webpackChunkName: "panchang" */ './MuhuratModal'));
import ProfileSwitcher, { AddFamilyMemberModal } from './ProfileSwitcher';
import AppShellFallback from './AppShellFallback';
// Cosmic Void v1 — mobile-only refactored components (see /app/design-system.md)
import ActivationNudge from './ActivationNudge';
import AppDownloadBanner from './AppDownloadBanner';
import KundaliSnapshotCard from './snapshot/KundaliSnapshotCard';
import { fetchSnapshot, emitSnapshotViewed } from '../utils/snapshotService';
import HeroInsightBanner from './dashboard-v2/HeroInsightBanner';
import FamilyStrip from './dashboard-v2/FamilyStrip';
import ContinuePath from './dashboard-v2/ContinuePath';
import CvSectionHeader from './dashboard-v2/CvSectionHeader';
import ScrollTile from './dashboard-v2/ScrollTile';
import DeepCard from './dashboard-v2/DeepCard';
import TransitInsightsIcon from './dashboard-v2/TransitInsightsIcon';
// Redesign v2 primitives (hero-led product home)


import ToolsStrip from './dashboard-v2/ToolsStrip';
// Tool modals
const NumerologyModal = React.lazy(() => import(/* webpackChunkName: "panchang" */ './dashboard-v2/NumerologyModal'));
const PanchangScheduleModal = React.lazy(() => import(/* webpackChunkName: "panchang" */ './dashboard-v2/PanchangScheduleModal'));
const ToolsMoreModal = React.lazy(() => import(/* webpackChunkName: "panchang" */ './dashboard-v2/ToolsMoreModal'));
// Additional lucide icons for the new mobile sections.
import { Hash, CalendarDays, Grid3X3, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DiyaPrasadCard from './DiyaPrasadCard';
import Icon from './ui/Icon';
import { useModal } from '../contexts/ModalContext';
import { useUserMode } from '../contexts/UserModeContext';
import { useToast } from '../hooks/use-toast';
import { getBackendUrl } from '../config/api';
const SessionModeController = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './session/SessionModeController'));
const ChitraguptGuidanceModal = React.lazy(() => import(/* webpackChunkName: "session-chat" */ './session/ChitraguptGuidanceModal'));
import { buildProfileSubject } from '../utils/sessionSubject';
import { INTENT_PROMPTS, seedIntentChat, showEntryCtaAbovePrasad } from '../utils/intentBridge';
import AddBirthPlaceCard from './AddBirthPlaceCard';
import { needsBirthplace } from '../utils/birthPlace';
import { karmaToMinutes } from '../utils/karmaHelpers';

// PremiumCard component
const PremiumCard = ({ children, className = '', variant = 'default', onClick, interactive = false, glow = false }) => {
  const variants = {
    default: 'jp-card',
    glass: 'jp-card',
    premium: 'jp-card border border-purple-300/20',
    minimal: 'bg-white/95 border border-gray-100',
    glow: 'jp-card border border-blue-300/30 shadow-2xl shadow-blue-500/10'
  };

  const handleClick = (e) => {
    if (onClick) { e.preventDefault(); e.stopPropagation(); onClick(); }
  };

  const Component = motion.div;

  return (
    <Component
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={interactive ? { y: -4, scale: 1.01 } : {}}
      whileTap={interactive ? { scale: 0.98 } : {}}
      onClick={interactive ? handleClick : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(e); } : undefined}
      className={`
        ${variants[glow ? 'glow' : variant]} 
        rounded-3xl p-6 transition-all duration-500 ease-out
        ${interactive ? 'cursor-pointer hover:shadow-2xl hover:shadow-purple-500/20 w-full text-left' : ''}
        ${glow ? 'shadow-xl shadow-blue-500/20' : 'shadow-xl'}
        ${className}
      `}
    >
      {children}
    </Component>
  );
};

// ── RecentClientsPanel — compact list shown in astrologer dashboard ──────────
const RecentClientsPanel = ({ onNavigate }) => {
  const charts = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('savedCharts') || '[]').slice(0, 3); }
    catch { return []; }
  }, []);

  if (charts.length === 0) {
    return (
      <div className="jp-card" style={{ padding: '14px 16px' }}>
        <p style={{
          fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 600,
          color: 'var(--text-tertiary)', textTransform: 'uppercase',
          letterSpacing: '0.13em', marginBottom: 10,
        }}>RECENT CLIENTS</p>
        <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, color: 'var(--text-tertiary)', textAlign: 'center', padding: '12px 0' }}>
          No clients yet
        </p>
        <button
          onClick={() => onNavigate('clients')}
          style={{
            width: '100%', height: 32, borderRadius: 8, border: 'none',
            background: 'linear-gradient(135deg,#6366f1,#a855f7)',
            color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer',
          }}
        >+ Add First Client</button>
      </div>
    );
  }

  return (
    <div className="jp-card" style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{
          fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 600,
          color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.13em',
        }}>RECENT CLIENTS</p>
        <button
          onClick={() => onNavigate('clients')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: 'var(--gold-primary)',
            display: 'flex', alignItems: 'center', gap: 3,
          }}
        >
          View all <ChevronRight style={{ width: 10, height: 10 }} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {charts.map((chart, i) => (
          <button
            key={i}
            onClick={() => onNavigate('clients')}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '6px 8px', borderRadius: 8, border: 'none',
              background: 'rgba(255,255,255,0.04)', cursor: 'pointer',
              transition: 'background 0.15s', textAlign: 'left',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
          >
            <div style={{
              width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,rgba(99,102,241,0.4),rgba(168,85,247,0.4))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: '#a5b4fc', fontWeight: 700, fontSize: 11 }}>
                {(chart.name || '?').charAt(0).toUpperCase()}
              </span>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{
                fontFamily: 'Cinzel, serif', fontSize: 11, color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                margin: 0,
              }}>{chart.name}</p>
              <p style={{
                fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: 'var(--text-tertiary)',
                margin: 0,
              }}>{chart.date} · {chart.place}</p>
            </div>
            <Telescope style={{ width: 12, height: 12, color: 'var(--text-tertiary)', flexShrink: 0 }} />
          </button>
        ))}
      </div>
    </div>
  );
};

const PremiumDashboard = ({ userInfo, kundliData, onNavigate, onNewChart, onViewSavedCharts, onOpenChart, viewingOtherChart, onBackToMyChart, isMobile, familyProfiles = [], activeFamilyProfile = null, familyKundliData = null, onSwitchToFamilyProfile, onSwitchToSelf, onAddFamilyProfile, onUpdateFamilyProfile, onDeleteFamilyProfile, onBirthplaceSaved }) => {
  const [greeting, setGreeting] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [expandedCard, setExpandedCard] = useState(null);
  const [currentMahadasha, setCurrentMahadasha] = useState(null);
  // BIRTH-TIME-1: null | {kind:'future', mahadasha, next} | {kind:'invalid_time'}
  const [dashaCardState, setDashaCardState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddFamilyModal, setShowAddFamilyModal] = useState(false);
  const [authFeatureName, setAuthFeatureName] = useState('');
  const [showSessionMode, setShowSessionMode] = useState(false);
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [showCosmicStore, setShowCosmicStore] = useState(false);
  const [storeFocusPackId, setStoreFocusPackId] = useState(null);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState(false);
  const [walletMinutes, setWalletMinutes] = useState(() => {
    try { return karmaToMinutes(parseInt(localStorage.getItem('jp_karma_balance') || '0', 10)); } catch { return 0; }
  });
  const [karmaBalance, setKarmaBalance] = useState(() => {
    try { return parseInt(localStorage.getItem('jp_karma_balance') || '0', 10); } catch { return 0; }
  });
  // purchased_balance from the wallet (null = loading). Trial/reward karma lands
  // in promo_balance, so purchased === 0 with a positive total means the wallet
  // is FREE karma only — a pre-payer. Used to keep the payer layout unchanged.
  const [purchasedBalance, setPurchasedBalance] = useState(null);
  // Count of completed readings (null = loading). Pre-session users (0) get the
  // ChitraGupt entry CTA ABOVE Daily Prasad; after the first session the normal
  // order (Prasad as the retention loop) is restored.
  const [sessionCount, setSessionCount] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [horaData, setHoraData] = useState(null);
  const [showHoraSchedule, setShowHoraSchedule] = useState(false);
  const [showMuhuratModal, setShowMuhuratModal] = useState(false);
  const [panchangData, setPanchangData] = useState(null);
  const [muhuratData, setMuhuratData] = useState(null);
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [showAllTools, setShowAllTools] = useState(false);
  const [trialUsed, setTrialUsed] = useState(false);
  const [showNumerology, setShowNumerology] = useState(false);
  const [showPanchang, setShowPanchang] = useState(false);
  const [showToolsMore, setShowToolsMore] = useState(false);
  const [reportPrice, setReportPrice] = useState(250);
  useEffect(() => {
    fetch(`${getBackendUrl()}/api/razorpay-subscriptions/plans`)
      .then(r => r.json())
      .then(data => {
        if (data.standalone_report_prices?.LIFE_GUIDANCE) {
          setReportPrice(data.standalone_report_prices.LIFE_GUIDANCE);
        }
      })
      .catch(() => {});
  }, []);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { openModal, closeModal } = useModal();

  // ── Effective data: use family member data when a profile is active ────────
  const effectiveUserInfo = activeFamilyProfile
    ? {
        ...userInfo,
        name: activeFamilyProfile.name,
        gender: activeFamilyProfile.gender || userInfo?.gender,
        date: activeFamilyProfile.dob,
        time: activeFamilyProfile.tob,
        place: activeFamilyProfile.pob,
        latitude: activeFamilyProfile.lat,
        longitude: activeFamilyProfile.lon,
        _isFamilyProfile: true,
      }
    : userInfo;

  // Never fall back to the owner's chart while a family chart loads (B2):
  // that paired the member's name with the owner's lagna/dasha/report.
  const effectiveKundliData = activeFamilyProfile
    ? (familyKundliData?.charts?.D1 || null)
    : kundliData;
  // A saved/client chart replaces the Home body; a family profile does not —
  // the mobile Home renders for the family member (B3: it used to go blank).
  const viewingClientChart = viewingOtherChart && !activeFamilyProfile;
  const dashaReqRef = React.useRef(0);

  const { dashboard, isAstrologerMode } = useUserMode();
  
  const urlParams = new URLSearchParams(window.location.search);
  const testMode = urlParams.get('test') === 'true';
  const tierOverride = urlParams.get('tier') ? parseInt(urlParams.get('tier')) : null;
  
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated || testMode || false;
  const startGoogleAuth = auth?.startGoogleAuth;
  
  const tierLevel = tierOverride !== null ? tierOverride : (userInfo?.tier_level || userInfo?.subscription_tier || 0);
  const isPremiumMember = tierLevel >= 1;
  const isSageMember = tierLevel >= 2;
  
  const currentDasha = userInfo?.current_dasha;
  const dashaStatus = currentDasha?.status || (currentDasha?.mahadasha ? `${currentDasha.mahadasha} Mahadasha Active` : null);
  
  const [showSessionAuthWall, setShowSessionAuthWall] = useState(false);
  
  const handleAuthGatedAction = (action, featureName) => {
    if (!isAuthenticated) { setAuthFeatureName(featureName); setShowAuthModal(true); return; }
    action();
  };

  const fetchWalletBalance = async () => {
    if (!userInfo?.user_id) return;
    setWalletLoading(true);
    try {
      // JP-17: wallet endpoint now requires the owner's session token.
      const token = localStorage.getItem('jyotish_session_token');
      const response = await fetch(`${getBackendUrl()}/api/payments/user/${userInfo.user_id}/wallet`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        const karma = data.karma_balance || (data.wallet_minutes || 0) * 100;
        const minutes = karmaToMinutes(karma);
        setKarmaBalance(karma);
        setWalletMinutes(minutes);
        setPurchasedBalance(Number(data.purchased_balance) || 0);
        try { localStorage.setItem('jp_karma_balance', String(karma)); localStorage.setItem('jp_wallet_minutes', String(minutes)); } catch {}
        return minutes;
      }
    } catch (error) { console.error('[WALLET] Failed to fetch balance:', error); }
    finally { setWalletLoading(false); }
    return 0;
  };

  const handleWalletRefresh = async () => {
    const newBalance = await fetchWalletBalance();
    return newBalance;
  };

  useEffect(() => {
    if (userInfo?.user_id) { fetchWalletBalance(); }
  }, [userInfo?.user_id]); // eslint-disable-line

  // Re-fetch when a purchase elsewhere (e.g. /payment-complete) credits
  // karma — without this the balance here goes stale until a full reload.
  useEffect(() => {
    const onRefresh = () => fetchWalletBalance();
    window.addEventListener('karma:refresh', onRefresh);
    return () => window.removeEventListener('karma:refresh', onRefresh);
  }, [userInfo?.user_id]); // eslint-disable-line

  // Count of completed readings — decides the pre-session card order (ChitraGupt
  // entry above Daily Prasad until the first session). Mirrors SavedSessionsList
  // so the count agrees (ended, non-cancelled sessions only).
  useEffect(() => {
    if (!userInfo?.user_id) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('jyotish_session_token');
        if (!token) { if (!cancelled) setSessionCount(0); return; }
        const res = await fetch(`${getBackendUrl()}/api/session/history?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) return; // leave null (unknown) — never assume 0 on a failed read
        const data = await res.json();
        const ended = (Array.isArray(data) ? data : []).filter(
          (s) => s.status === 'ended' && s.end_reason !== 'cancelled'
        );
        if (!cancelled) setSessionCount(ended.length);
      } catch { /* non-fatal — order falls back to the default */ }
    })();
    return () => { cancelled = true; };
  }, [userInfo?.user_id]); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('jyotish_session_token');
        if (!token) { if (!cancelled) setTrialUsed(false); return; }
        const res = await fetch(`${getBackendUrl()}/api/user/wallet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setTrialUsed(!!data.trial_used);
        }
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
  }, [userInfo?.user_id]);

  useEffect(() => {
    if (showAuthModal) { openModal('premium-auth-modal'); } else { closeModal('premium-auth-modal'); }
  }, [showAuthModal, openModal, closeModal]);

  useEffect(() => {
    if (showSessionAuthWall) { openModal('session-auth-wall'); } else { closeModal('session-auth-wall'); }
  }, [showSessionAuthWall, openModal, closeModal]);

  useEffect(() => {
    if (showGuidanceModal) { openModal('guidance-modal'); } else { closeModal('guidance-modal'); }
  }, [showGuidanceModal, openModal, closeModal]);

  const handleGuidanceClick = () => {
    if (!isAuthenticated) { setShowSessionAuthWall(true); return; }
    const hasBalance = karmaBalance > 0;
    const isPremium = userInfo?.has_active_subscription || false;
    if (hasBalance || isPremium) {
      toast({ title: "Starting Session", description: `You have ${karmaBalance.toLocaleString()} Karma available` });
      setShowSessionMode(true);
    } else { setShowSessionMode(true); /* karma-only: canonical flow + top-up nudge, not the retired ₹ modal */ }
  };

  useEffect(() => {
    const now = new Date();
    const hour = now.getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 17) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
    setCurrentDate(now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }));
    if (userInfo || activeFamilyProfile) { fetchCurrentMahadasha(); }
    fetchHoraData();
    fetchPanchangData();
    loadRecentProfiles();
  }, [activeFamilyProfile?.profile_id, effectiveUserInfo?.name, effectiveUserInfo?.date, effectiveUserInfo?.time, effectiveUserInfo?.place]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHoraData = async () => {
    try {
      const lat = userInfo?.latitude || 23.2599;
      const lon = userInfo?.longitude || 77.4126;
      const response = await fetch(`${getBackendUrl()}/api/hora-schedule?latitude=${lat}&longitude=${lon}`);
      if (response.ok) { const data = await response.json(); setHoraData(data); }
    } catch (error) { console.error('Error fetching Hora data:', error); }
  };

  const fetchPanchangData = async () => {
    try {
      const lat = userInfo?.latitude || 23.2599;
      const lon = userInfo?.longitude || 77.4126;
      const response = await fetch(`${getBackendUrl()}/api/home-daily?latitude=${lat}&longitude=${lon}`);
      if (response.ok) {
        const data = await response.json();
        setPanchangData(data.panchang || null);
        setMuhuratData({
          current: data.current_choghadiya || null,
          schedule: data.choghadiya_schedule || [],
          rahu_kaal: data.timing?.rahu_kaal || null,
          sunrise: data.timing?.sunrise,
          sunset: data.timing?.sunset
        });
      }
    } catch (error) { console.error('Error fetching Panchang data:', error); }
  };

  const loadRecentProfiles = () => {
    try {
      const stored = localStorage.getItem('jyotish_recent_profiles');
      if (stored) { setRecentProfiles(JSON.parse(stored).slice(0, 5)); }
    } catch (e) { console.error('Error loading recent profiles:', e); }
  };

  useEffect(() => {
    if (viewingOtherChart && effectiveUserInfo?.name) {
      try {
        const stored = localStorage.getItem('jyotish_recent_profiles');
        let profiles = stored ? JSON.parse(stored) : [];
        const existing = profiles.findIndex(p => p.name === effectiveUserInfo.name && p.date === effectiveUserInfo.date);
        if (existing !== -1) profiles.splice(existing, 1);
        profiles.unshift({
          name: effectiveUserInfo.name, date: effectiveUserInfo.date, time: effectiveUserInfo.time,
          place: effectiveUserInfo.place, gender: effectiveUserInfo.gender,
          latitude: effectiveUserInfo.latitude, longitude: effectiveUserInfo.longitude,
          viewedAt: new Date().toISOString()
        });
        profiles = profiles.slice(0, 10);
        localStorage.setItem('jyotish_recent_profiles', JSON.stringify(profiles));
        setRecentProfiles(profiles.slice(0, 5));
      } catch (e) { console.error('Error saving recent profile:', e); }
    }
  }, [viewingOtherChart, effectiveUserInfo?.name]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlIntent = (params.get('intent') || '').toLowerCase();
      const stashedIntent = (sessionStorage.getItem('jp_entry_intent') || '').toLowerCase();
      const intent = urlIntent || stashedIntent;
      if (!intent) return;

      if (sessionStorage.getItem('jp_intent_processed') === intent) {
        if (urlIntent) {
          params.delete('intent');
          const newSearch = params.toString();
          const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }
        return;
      }

      // INTENT_PROMPTS / seedIntentChat are shared with the onboarding router
      // (utils/intentBridge) so the two entry paths can never drift. Onboarding
      // usually handles ad arrivals first (setting jp_intent_processed, guarded
      // above); this remains the fallback for a user who reaches the dashboard
      // with an unprocessed intent.
      let destination = null;

      if (intent === 'matchmaking') {
        destination = 'compatibility';
      } else if (INTENT_PROMPTS[intent]) {
        seedIntentChat(intent);
        destination = 'chat';
      }

      sessionStorage.setItem('jp_intent_processed', intent);
      sessionStorage.removeItem('jp_entry_intent');
      if (urlIntent) {
        params.delete('intent');
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
        window.history.replaceState({}, '', newUrl);
      }

      trackEvent('intent_bridge_fired', {
        intent,
        destination: destination || 'none',
        source: urlIntent ? 'url' : 'session_stash',
      });

      if (destination && typeof onNavigate === 'function') {
        onNavigate(destination);
      }
    } catch (e) {
      console.warn('[INTENT-BRIDGE] Failed:', e);
    }
  }, []); // eslint-disable-line

  const fetchCurrentMahadasha = async () => {
    if (!effectiveUserInfo) return;
    // Stale-response guard (B2): only the latest request for the CURRENT
    // subject may write; the previous subject's period is cleared at once.
    const reqId = ++dashaReqRef.current;
    setCurrentMahadasha(null);
    setDashaCardState(null);
    setLoading(true);
    try {
      const body = { date: effectiveUserInfo.date, time: effectiveUserInfo.time, place: effectiveUserInfo.place, gender: effectiveUserInfo.gender };
      // Stored coordinates, when the subject has them, so the card is built from the same
      // place as the saved chart instead of a re-geocode of the place text (#860 contract).
      if (effectiveUserInfo.latitude != null && effectiveUserInfo.longitude != null) {
        body.latitude = effectiveUserInfo.latitude;
        body.longitude = effectiveUserInfo.longitude;
      }
      const response = await fetch(`${getBackendUrl()}/api/hierarchical-dasha?years=120`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const payload = await response.json().catch(() => null);
      const special = dashaCardStateFrom(response.ok, payload);
      if (special) {
        if (reqId === dashaReqRef.current) {
          setDashaCardState(special);
          if (special.kind === 'future') {
            trackEvent('chapter_card_future_birth_shown', {
              surface: 'home_web', relation: activeFamilyProfile?.relation_type || 'self',
            });
          }
        }
      } else if (response.ok && payload) {
        const data = payload;
        const now = new Date();
        const parseApiDate = (dateString) => {
          const [day, month, year] = dateString.split('-');
          const monthNames = { 'Jan':0,'Feb':1,'Mar':2,'Apr':3,'May':4,'Jun':5,'Jul':6,'Aug':7,'Sep':8,'Oct':9,'Nov':10,'Dec':11 };
          return new Date(parseInt(year), monthNames[month], parseInt(day));
        };
        const currentDasha = data.dasha_hierarchy?.find(period => {
          const startDate = parseApiDate(period.start_date);
          const endDate = parseApiDate(period.end_date);
          return now >= startDate && now <= endDate;
        });
        if (currentDasha && reqId === dashaReqRef.current) { setCurrentMahadasha(currentDasha); }
      }
    } catch (error) { console.error('Error fetching current Mahadasha:', error); }
    finally { if (reqId === dashaReqRef.current) setLoading(false); }
  };

  const [todaysInsight] = useState(() => {
    const insights = [
      { text: 'Jupiter\'s blessing brings wisdom today', icon: '🌟', color: 'text-yellow-400' },
      { text: 'Venus alignment favors relationships', icon: '💝', color: 'text-pink-400' },
      { text: 'Mars energy boosts your confidence', icon: '⚡', color: 'text-red-400' },
      { text: 'Mercury enhances communication', icon: '🗣️', color: 'text-green-400' },
      { text: 'Moon phase supports intuition', icon: '🌙', color: 'text-blue-400' }
    ];
    return insights[Math.floor(Math.random() * insights.length)];
  });

  const ENABLE_SESSION_MODE = true;
  const freeInsightUsed = userInfo?.free_insights_used >= 1;
  const hasWalletBalance = walletMinutes > 0;

  // Dashboard "Ask ChitraGupt" CTAs route to the /chitragupt entry card (the
  // free-first screen), NOT the SessionPlansModal pricing sheet. The pricing
  // sheet stays for the change-plan / paid-topup path inside the session flow.
  // Every affordability state (trial, karma, or none) is handled on the entry
  // card, so this navigates unconditionally once the user is signed in.
  const handleAskChitragupt = () => {
    if (!isAuthenticated) { setShowSessionAuthWall(true); return; }
    onNavigate?.('chat', { source: 'dashboard_card' });
  };

  // Kundali Snapshot for Today-First Home (D4)
  const [homeSnapshot, setHomeSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);

  const activeProfileId = activeFamilyProfile?.profile_id || 'self';
  const effectiveName = activeFamilyProfile?.name || userInfo?.name || 'Seeker';

  useEffect(() => {
    let cancelled = false;
    const loadHomeSnapshot = async () => {
      setSnapshotLoading(true);
      try {
        const lang = localStorage.getItem('app_lang') || localStorage.getItem('jyotish_report_lang') || 'hinglish';
        const intent = sessionStorage.getItem('jp_entry_intent') || 'general';
        const data = await fetchSnapshot({
          profileId: activeProfileId,
          intent,
          lang: lang === 'hi' ? 'hi' : (lang === 'en' ? 'en' : 'hinglish'),
          name: effectiveName,
        });
        if (!cancelled) {
          setHomeSnapshot(data);
          emitSnapshotViewed({ intent, profileId: activeProfileId });
        }
      } catch (err) {
        // snapshot load failed silently; fallback or skeleton resolves
      } finally {
        if (!cancelled) setSnapshotLoading(false);
      }
    };

    loadHomeSnapshot();
    return () => { cancelled = true; };
  }, [activeProfileId, effectiveName]);

  const handleSnapshotAskQuestion = (questionText, questionIntent, { freeQuestion } = {}) => {
    try {
      localStorage.setItem(
        'pending_chat_message',
        JSON.stringify({
          text: questionText,
          createdAt: Date.now(),
          intent: questionIntent || 'general',
          free_question: !!freeQuestion,
        })
      );
      sessionStorage.setItem('pendingChatQuestion', questionText);
      sessionStorage.setItem('jp_entry_intent', questionIntent || 'general');
      if (freeQuestion) {
        sessionStorage.setItem('free_question_mode', 'true');
      }
    } catch (e) {
      console.warn('[HOME-SNAPSHOT] Staging error:', e);
    }
    handleAskChitragupt();
  };

  // Pre-session, non-payer users get the ChitraGupt entry CTA ABOVE the Daily
  // Prasad card (item 3) so the free-first session is the first thing they see;
  // after the first session the normal order (Prasad = the retention loop) is
  // restored. Payers (purchased karma or an active subscription) keep the normal
  // order — no layout change. sessionCount === null (loading/unknown) → no flip.
  const isPayer = (purchasedBalance != null && purchasedBalance > 0)
    || !!userInfo?.has_active_subscription
    || tierLevel >= 1;
  const showEntryCtaFirst = showEntryCtaAbovePrasad({ sessionCount, isPayer });

  const services = [
    {
      id: 'horoscope', title: 'Daily Horoscope', subtitle: 'Daily guidance',
      icon: Sun, color: 'from-yellow-500 to-orange-500',
      action: () => onNavigate('horoscope'),
      description: 'Daily, weekly & monthly predictions for all 12 zodiac signs', requiresAuth: false
    },
    {
      id: 'compatibility', title: 'Match Making', subtitle: 'Guna Milan',
      icon: Heart, color: 'from-pink-500 to-rose-500',
      action: () => onNavigate('compatibility'), badge: 'FREE',
      description: 'Traditional Vedic compatibility analysis for perfect matches', requiresAuth: false
    },
    {
      id: 'chart', title: 'Birth Chart', subtitle: 'Vedic Kundli',
      icon: Eye, color: 'from-emerald-500 to-teal-500',
      action: () => isMobile
        ? onNavigate('workstation')
        : onNavigate('enhanced-charts'),
      description: 'Interactive Birth Chart with Jaimini & KP Analysis',
      requiresAuth: false, isExpandable: false,
    },
    ...(hasWalletBalance ? [{
      id: 'guidance', title: 'ChitraGupt Guidance',
      subtitle: 'Start New Session',
      icon: Timer, color: 'from-purple-500 to-pink-500',
      action: handleAskChitragupt, badge: 'ACTIVE', badgePulse: true,
      description: 'Deep-Dive Session: Full analytical brain with pre-reading & SAV/BAV synthesis',
      requiresAuth: false, isPremium: true, isVIP: true
    }] : [
      freeInsightUsed ? {
        id: 'guidance', title: 'ChitraGupt Guidance', subtitle: 'Unlock Deep-Dive Sessions',
        icon: Timer, color: 'from-purple-500 to-pink-500',
        action: handleAskChitragupt, badge: 'UPGRADE',
        description: 'Book a timed guidance session with ChitraGupt — Express, Detailed, or Complete',
        requiresAuth: false, isPremium: true
      } : {
        id: 'chat', title: 'Free Chat', subtitle: '1 Free Initial Insight',
        icon: MessageCircle, color: 'from-blue-500 to-cyan-500',
        action: () => { onNavigate('chat'); }, badge: 'FREE',
        description: 'Get 1 free initial insight from ChitraGupt before upgrading', requiresAuth: false
      }
    ]),
    ...(!hasWalletBalance && !freeInsightUsed ? [{
      id: 'guidance', title: 'ChitraGupt Guidance', subtitle: 'AI-Powered Sessions',
      icon: Timer, color: 'from-purple-500 to-pink-500',
      action: handleAskChitragupt, badge: 'PREMIUM',
      description: 'Book a timed guidance session with ChitraGupt — Express, Detailed, or Complete',
      requiresAuth: false, isPremium: true
    }] : []),
    ...(isPremiumMember ? [{
      id: 'predictions', title: 'Divine Predictions', subtitle: 'Personalized',
      icon: Star, color: 'from-purple-500 to-violet-500',
      action: () => onNavigate('predictions'), badge: isSageMember ? 'SAGE' : 'SEEKER',
      description: 'Personalized predictions based on your complete birth chart with planet-specific insights',
      requiresAuth: true, isPremium: true
    }] : []),
    {
      id: 'reports', title: 'Download Reports', subtitle: 'PDF & PNG',
      icon: Download, color: 'from-amber-500 to-orange-500',
      action: () => onNavigate('reports'),
      badge: 'PDF',
      description: 'Comprehensive PDF reports detailing your birth chart and timeline predictions',
      requiresAuth: false
    }
  ];

  /* ─── Handmade Tool Icons ─────────────────────────────────────────────────── */

  // Ashtakvarga — 8-spoke wheel (Ashta = 8) with outer ring + bindu
  const IcoAshtakvarga = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      {[0,45,90,135].map(a => {
        const r = a * Math.PI / 180;
        return <line key={a} x1={10} y1={10} x2={+(10+8.2*Math.cos(r)).toFixed(2)} y2={+(10+8.2*Math.sin(r)).toFixed(2)}
          stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />;
      })}
      {[22.5,67.5,112.5,157.5].map(a => {
        const r = a * Math.PI / 180;
        return <line key={a} x1={10} y1={10} x2={+(10+8.2*Math.cos(r)).toFixed(2)} y2={+(10+8.2*Math.sin(r)).toFixed(2)}
          stroke="currentColor" strokeWidth="0.65" strokeLinecap="round" opacity="0.65" />;
      })}
      <circle cx="10" cy="10" r="5.2" fill="none" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
      {[0,45,90,135,180,225,270,315].map(a => {
        const r = a * Math.PI / 180;
        return <circle key={a} cx={+(10+8.2*Math.cos(r)).toFixed(2)} cy={+(10+8.2*Math.sin(r)).toFixed(2)} r="1" fill="currentColor" opacity="0.7"/>;
      })}
      <circle cx="10" cy="10" r="1.9" fill="currentColor"/>
    </svg>
  );

  // Varga Ashtakvarga — 3 nested rotated squares (D-chart stack)
  const IcoVargaAshtakvarga = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <rect x="2.5" y="2.5" width="15" height="15" stroke="currentColor" strokeWidth="0.8" rx="0.5"/>
      <rect x="2.5" y="2.5" width="15" height="15" stroke="currentColor" strokeWidth="0.7" opacity="0.65"
        transform="rotate(22.5 10 10)"/>
      <polygon points="10,5 15,10 10,15 5,10" stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.9"/>
      <circle cx="10" cy="10" r="1.6" fill="currentColor"/>
    </svg>
  );

  // Transit Chart — orbit ring with moving planet
  const IcoTransitChart = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <ellipse cx="10" cy="10" rx="8.2" ry="4.8" stroke="currentColor" strokeWidth="0.8" opacity="0.7"/>
      <ellipse cx="10" cy="10" rx="5.5" ry="3.2" stroke="currentColor" strokeWidth="0.55" opacity="0.45"/>
      <circle cx="10" cy="10" r="2.2" fill="currentColor" opacity="0.9"/>
      <line x1="6.5" y1="9" x2="13.5" y2="11" stroke="currentColor" strokeWidth="0.7" opacity="0.5"/>
      <circle cx="18.2" cy="10" r="1.3" fill="currentColor"/>
      <ellipse cx="10" cy="10" rx="8.2" ry="4.8" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 2.5"
        opacity="0.35" strokeLinecap="round"/>
    </svg>
  );

  // Divisional Charts — North Indian kundali
  const IcoDivisionalCharts = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <rect x="1.5" y="1.5" width="17" height="17" stroke="currentColor" strokeWidth="0.85" rx="0.5"/>
      <line x1="1.5" y1="1.5" x2="10" y2="10" stroke="currentColor" strokeWidth="0.75"/>
      <line x1="18.5" y1="1.5" x2="10" y2="10" stroke="currentColor" strokeWidth="0.75"/>
      <line x1="1.5" y1="18.5" x2="10" y2="10" stroke="currentColor" strokeWidth="0.75"/>
      <line x1="18.5" y1="18.5" x2="10" y2="10" stroke="currentColor" strokeWidth="0.75"/>
      <line x1="10" y1="1.5" x2="10" y2="18.5" stroke="currentColor" strokeWidth="0.55" opacity="0.45"/>
      <line x1="1.5" y1="10" x2="18.5" y2="10" stroke="currentColor" strokeWidth="0.55" opacity="0.45"/>
      <circle cx="10" cy="10" r="1.4" fill="currentColor"/>
    </svg>
  );

  // Planetary Strength — ShadBala
  const IcoPlanetaryStrength = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <circle cx="10" cy="10" r="2.5" fill="currentColor"/>
      {[
        { a:  90, l: 7.5 }, { a: 30,  l: 5.8 }, { a: 330, l: 6.8 },
        { a: 270, l: 4.8 }, { a: 210, l: 5.2 }, { a: 150, l: 6.2 },
      ].map(({ a, l }) => {
        const r = (a - 90) * Math.PI / 180;
        return (
          <line key={a}
            x1={+(10 + 2.9 * Math.cos(r)).toFixed(2)} y1={+(10 + 2.9 * Math.sin(r)).toFixed(2)}
            x2={+(10 + l   * Math.cos(r)).toFixed(2)} y2={+(10 + l   * Math.sin(r)).toFixed(2)}
            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        );
      })}
    </svg>
  );

  // Karma Store — 6-petal lotus with centre gem
  const IcoKarmaStore = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      {[0,60,120,180,240,300].map(a => {
        const r = (a - 90) * Math.PI / 180;
        const cx = +(10 + 4.6 * Math.cos(r)).toFixed(2);
        const cy = +(10 + 4.6 * Math.sin(r)).toFixed(2);
        return <ellipse key={a} cx={cx} cy={cy} rx="2.6" ry="4.4"
          stroke="currentColor" strokeWidth="0.75" fill="currentColor" fillOpacity="0.18"
          transform={`rotate(${a}, ${cx}, ${cy})`}/>;
      })}
      <circle cx="10" cy="10" r="2.4" fill="currentColor"/>
    </svg>
  );

  // My Subscription — ornate crown
  const IcoMySubscription = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <polyline points="1.8,16.5 1.8,8.5 6,13 10,3.5 14,13 18.2,8.5 18.2,16.5"
        stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round"/>
      <line x1="1.8" y1="16.5" x2="18.2" y2="16.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      <circle cx="10" cy="3.8" r="1.2" fill="currentColor"/>
      <circle cx="6.2" cy="13.2" r="0.9" fill="currentColor" opacity="0.8"/>
      <circle cx="13.8" cy="13.2" r="0.9" fill="currentColor" opacity="0.8"/>
    </svg>
  );

  // Profile Settings — astrolabe compass
  const IcoProfileSettings = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <circle cx="10" cy="10" r="8.3" stroke="currentColor" strokeWidth="0.8" opacity="0.55"/>
      {[0,90,180,270].map(a => {
        const r = a * Math.PI / 180;
        return <line key={a}
          x1={+(10 + 6.5*Math.cos(r)).toFixed(2)} y1={+(10 + 6.5*Math.sin(r)).toFixed(2)}
          x2={+(10 + 8.3*Math.cos(r)).toFixed(2)} y2={+(10 + 8.3*Math.sin(r)).toFixed(2)}
          stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>;
      })}
      {[45,135,225,315].map(a => {
        const r = a * Math.PI / 180;
        return <line key={a}
          x1={+(10 + 7.0*Math.cos(r)).toFixed(2)} y1={+(10 + 7.0*Math.sin(r)).toFixed(2)}
          x2={+(10 + 8.3*Math.cos(r)).toFixed(2)} y2={+(10 + 8.3*Math.sin(r)).toFixed(2)}
          stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" opacity="0.55"/>;
      })}
      <circle cx="10" cy="8.2" r="1.8" fill="currentColor"/>
      <path d="M6.5 15 Q6.5 11.5 10 11.5 Q13.5 11.5 13.5 15" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round"/>
    </svg>
  );

  // Download Reports — ancient scroll with download arrow
  const IcoDownloadReports = ({ style }) => (
    <svg viewBox="0 0 20 20" fill="none" style={{ display:'block', ...style }}>
      <rect x="4.5" y="4" width="11" height="13" rx="1.2" stroke="currentColor" strokeWidth="0.85"/>
      <ellipse cx="10" cy="4" rx="3.5" ry="1.3" stroke="currentColor" strokeWidth="0.75" fill="currentColor" fillOpacity="0.12"/>
      <ellipse cx="10" cy="17" rx="3.5" ry="1.3" stroke="currentColor" strokeWidth="0.75" fill="currentColor" fillOpacity="0.12"/>
      <line x1="7" y1="7.5" x2="13" y2="7.5" stroke="currentColor" strokeWidth="0.7" opacity="0.55"/>
      <line x1="7" y1="9.5" x2="11.5" y2="9.5" stroke="currentColor" strokeWidth="0.7" opacity="0.4"/>
      <line x1="10" y1="11" x2="10" y2="15" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
      <polyline points="7.8,13.2 10,15.4 12.2,13.2" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );

  // Past Readings — Lucide History icon wrapper styled to fit tools grid
  const IcoPastReadings = ({ style }) => (
    <History style={{ display: 'block', ...style }} />
  );

  // ── Seeker Tools — astrologer-only tools (Ashtakvarga, Varga Ashtakvarga,
  //    Planetary Strength) are deliberately excluded here.
  //    They appear in AstrologerDashboard for astrologer mode users.
  const advancedTools = [
    { title: 'Transit Chart',    subtitle: 'Current Gochar',        icon: IcoTransitChart,     action: () => onNavigate('transit') },
    { title: 'Janam Kundali',    subtitle: isAstrologerMode ? 'D1, D9, D10, D7, D12' : 'Lagna & Chandra Kundali', icon: IcoDivisionalCharts, action: () => onNavigate('enhanced-charts') },
    { title: 'Karma Store',      subtitle: 'Plans & Points',        icon: IcoKarmaStore,       action: () => setShowCosmicStore(true) },
    { title: 'My Subscription',  subtitle: 'Manage & History',      icon: IcoMySubscription,   action: () => handleAuthGatedAction(() => setShowSubscriptionManager(true), 'Subscription Manager') },
    { title: 'Profile Settings', subtitle: 'Birth Details',         icon: IcoProfileSettings,  action: () => onNavigate('profile') },
    { title: 'Download Reports', subtitle: isAuthenticated ? 'PDF & PNG' : 'Sign in', icon: IcoDownloadReports, action: () => onNavigate('reports') },
    { title: 'Past Readings',    subtitle: 'Saved Sessions',        icon: IcoPastReadings,     action: () => onNavigate('saved-sessions') },
  ];

  // On mobile, show only first 4 tools unless expanded
  const visibleTools = isMobile && !showAllTools ? advancedTools.slice(0, 4) : advancedTools;

  // ── Astrologer mode: render full command-center dashboard ────────────────
  if (isAstrologerMode) {
    return (
      <React.Suspense fallback={<AppShellFallback />}>
        <AstrologerDashboard
          userInfo={userInfo}
          kundliData={kundliData}
          onNavigate={onNavigate}
          onNewChart={onNewChart}
          onViewSavedCharts={onViewSavedCharts}
          onOpenChart={onOpenChart}
        />
      </React.Suspense>
    );
  }

  return (
    // JyotishPuraan Design System: void black #06030f + purple cosmic depth — per README §4
    <div className="premium-dashboard min-h-screen relative overflow-hidden" style={{ background: 'var(--cosmic-gradient)' }}>
      {/* Starfield — twinkling parallax layers behind everything */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="stars-layer-1"></div>
        <div className="stars-layer-2"></div>
      </div>
      {/* Layered radial glows — gold and purple, never heavy shadows */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(139,92,246,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(212,164,24,0.04) 0%, transparent 50%)' }}></div>

      <ActivationNudge user={userInfo} />

      <div className="relative z-10 py-3 px-4 lg:px-8 space-y-3 lg:space-y-6 pb-24 md:pb-6 jp-content-container">

        {/* Own chart can't be computed for want of coordinates → ask for the
            place instead of failing silently / showing a chart error. */}
        {!viewingOtherChart && needsBirthplace(userInfo) && (
          <AddBirthPlaceCard userInfo={userInfo} onSaved={onBirthplaceSaved} />
        )}

        {viewingOtherChart && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/40 rounded-xl p-3 flex items-center justify-between"
            data-testid="viewing-other-chart-banner"
          >
            <div className="flex items-center space-x-3">
              <Eye className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-white font-medium text-sm">Viewing chart for <span className="text-amber-400">{effectiveUserInfo?.name}</span></p>
                <p style={{ color: 'rgba(255,255,255,0.60)' }} className="text-xs">{effectiveUserInfo?.date} • {effectiveUserInfo?.time} • {effectiveUserInfo?.place}</p>
              </div>
            </div>
            <button onClick={onBackToMyChart} className="jp-btn-secondary text-sm py-2" data-testid="back-to-my-chart-btn">
              Back to My Chart
            </button>
          </motion.div>
        )}
        
        {/* ═══════════════════════════════════════════════════════════════
            MOBILE — Cosmic Void v1 dashboard
        ═══════════════════════════════════════════════════════════════ */}
        {dashboard?.showCosmicSnapshot && !viewingClientChart && (
          <div
            className="md:hidden flex flex-col"
            style={{
              gap: '28px',
              paddingTop: '16px',
              paddingBottom: '32px',
            }}
          >
            

            <KundaliSnapshotCard
              snapshot={homeSnapshot}
              loading={snapshotLoading}
              userName={effectiveUserInfo?.name || userInfo?.name || 'Seeker'}
              lang={(() => {
                const s = localStorage.getItem('app_lang') || localStorage.getItem('jyotish_report_lang');
                return s === 'hi' ? 'hi' : (s === 'en' ? 'en' : 'hinglish');
              })()}
              onAskQuestion={handleSnapshotAskQuestion}
              trialAvailable={!userInfo?.free_trial_used && !userInfo?.trial_used && !userInfo?.has_active_subscription && karmaBalance <= 0}
              testId="home-today-snapshot-card"
            />

            {!isAstrologerMode && (
              <FamilyStrip
                selfName={userInfo?.name}
                familyProfiles={familyProfiles}
                activeProfile={activeFamilyProfile}
                onSelect={(profile) => {
                  if (profile === null) onSwitchToSelf?.();
                  else onSwitchToFamilyProfile?.(profile);
                }}
                onAddNew={() => setShowAddFamilyModal(true)}
              />
            )}

            {/* Reports Merchandising Card */}
            <div
              className="p-5 rounded-2xl border border-[var(--line)] bg-[var(--card)] backdrop-blur-md flex flex-col justify-between space-y-4"
              data-testid="home-reports-merch-card"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                  <Icon name="scroll" size={24} />
                </div>
                <div className="space-y-1">
                  <h4 style={{ fontFamily: '"Playfair Display", serif' }} className="font-bold text-white text-base">
                    Your Life Reading is ready
                  </h4>
                  <p className="text-xs text-[var(--muted)]">
                    A full PDF of your chart & timeline. See a free sample chapter before you unlock.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-[var(--line)]">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onNavigate?.('reports')}
                    className="py-2.5 px-5 text-sm font-bold text-[#2a1a12] rounded-xl bg-gradient-to-b from-[var(--gold-2)] to-[var(--gold-3)] shadow-lg hover:opacity-90 transition-opacity flex items-center gap-1.5"
                  >
                    Unlock <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      sessionStorage.setItem('preselected_preview_type', 'LIFE_GUIDANCE');
                      onNavigate?.('reports');
                    }}
                    className="text-xs font-semibold text-amber-200 hover:text-amber-100 transition-colors border-b border-dashed border-amber-200/50 pb-0.5"
                  >
                    View sample
                  </button>
                </div>
                <span className="text-xs font-medium text-[var(--dim)] text-right">
                  from ₹{reportPrice} <span className="block text-[10px] text-amber-200/50">(or 2,500 Karma)</span>
                </span>
              </div>
            </div>

            

            {userInfo && (
              <DiyaPrasadCard
                userInfo={userInfo}
                onRewardClaimed={() => {
                  try { window.dispatchEvent(new CustomEvent('karma:refresh')); } catch {}
                }}
                onNavigate={onNavigate}
              />
            )}

            <section>
              <CvSectionHeader title="Tools" testId="section-tools" />
              <ToolsStrip
                items={[
                  { id: 'numerology', icon: Hash,           label: 'Numerology', onClick: () => setShowNumerology(true) },
                  { id: 'choghadiya', icon: Grid3X3,        label: 'Choghadiya', onClick: () => setShowMuhuratModal(true) },
                  { id: 'panchang',   icon: CalendarDays,   label: 'Panchang',   onClick: () => setShowPanchang(true) },
                  { id: 'more',       icon: MoreHorizontal, label: 'More',       onClick: () => setShowToolsMore(true) },
                ]}
              />
            </section>
          </div>
        )}

        {viewingClientChart && (
          <div className="md:hidden cv-enter" style={{ paddingTop: '12px' }}>
            <p className="cv-label">Viewing chart</p>
            <h2
              className="cv-font-hero"
              style={{
                color: 'var(--cv-text-active)',
                fontSize: '22px',
                margin: '4px 0 0',
                fontWeight: 700,
              }}
            >
              {effectiveUserInfo?.name || '—'}
            </h2>
          </div>
        )}

        {/* DESKTOP ONLY — Kundali Snapshot Card (D5e / D4) */}
        {dashboard?.showCosmicSnapshot && !viewingClientChart && (
          <div className="hidden md:block mb-6">
            <KundaliSnapshotCard
              snapshot={homeSnapshot}
              loading={snapshotLoading}
              userName={effectiveUserInfo?.name || userInfo?.name || 'Seeker'}
              lang={(() => {
                const s = localStorage.getItem('app_lang') || localStorage.getItem('jyotish_report_lang');
                return s === 'hi' ? 'hi' : (s === 'en' ? 'en' : 'hinglish');
              })()}
              onAskQuestion={handleSnapshotAskQuestion}
              trialAvailable={!userInfo?.free_trial_used && !userInfo?.trial_used && !userInfo?.has_active_subscription && karmaBalance <= 0}
              testId="desktop-home-today-snapshot-card"
            />
          </div>
        )}
        {dashboard?.showCosmicSnapshot && viewingClientChart && (
          <div className="hidden md:block">
            <CosmicSnapshot
              panchangData={panchangData}
              muhuratData={muhuratData}
              horaData={horaData}
              loading={loading}
              onOpenMuhurat={() => setShowMuhuratModal(true)}
              headerSlot={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    {!viewingOtherChart ? (
                      <>
                        <div style={{
                          fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
                          fontStyle: 'italic', fontWeight: 400,
                          fontSize: 11, color: 'rgba(201,168,76,0.75)',
                          lineHeight: 1.1, marginBottom: 1, letterSpacing: '0.02em',
                        }}>
                          {greeting}
                        </div>
                        <div style={{
                          fontFamily: 'Cinzel, serif', fontSize: 14, fontWeight: 600,
                          color: 'var(--text-primary)', lineHeight: 1.15,
                        }}>
                          {effectiveUserInfo?.name?.split(' ')[0] || 'Seeker'} Ji
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontFamily: 'Cinzel, serif', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                          {effectiveUserInfo?.name?.split(' ')[0]}'s Chart
                        </div>
                        <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: 'rgba(255,255,255,0.60)', marginTop: 2 }}>
                          Born: {effectiveUserInfo?.date} · {effectiveUserInfo?.time}
                        </div>
                      </>
                    )}
                    {!viewingOtherChart && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <Calendar style={{ width: 7, height: 7, color: 'rgba(255,255,255,0.60)' }} />
                        <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: 'rgba(255,255,255,0.60)' }}>
                          {currentDate}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              }
            />
          </div>
        )}

        {/* ── Bento Grid (desktop only) ── */}
        <div className="jp-bento-grid hidden md:grid" style={{ marginTop: 'var(--bento-gap)' }}>
          
          {/* LEFT: Dasha + Tools */}
          <div className="jp-bento-col jp-bento-col-left">
            {!isAstrologerMode && (
              <div className="hidden md:block">
                <DashaTimelineCard currentMahadasha={currentMahadasha} loading={loading} onNavigate={onNavigate}
                  cardState={dashaCardState} subjectName={activeFamilyProfile ? effectiveUserInfo?.name : null} />
              </div>
            )}

            {dashboard?.showRecentCharts && (
              <RecentClientsPanel onNavigate={onNavigate} />
            )}

            {/* Seeker Tools — advanced/astrologer tools excluded */}
            <div className="jp-card" style={{ padding: '14px 16px' }}>
              <p style={{
                fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 600,
                color: 'rgba(255,255,255,0.60)', textTransform: 'uppercase',
                letterSpacing: '0.13em', marginBottom: 10,
              }}>TOOLS</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {visibleTools.map((tool) => (
                  <button
                    key={tool.title}
                    onClick={tool.action}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    data-testid={`tool-${tool.title.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--card-border-subtle)' }}>
                      <tool.icon style={{ width: 14, height: 14, color: 'var(--gold-primary)' }} />
                    </div>
                    <div style={{ textAlign: 'left', minWidth: 0 }}>
                      <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 11, fontWeight: 500, color: '#ffffff' }}>{tool.title}</p>
                      <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'rgba(255,255,255,0.60)' }}>{tool.subtitle}</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="md:hidden mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <button
                  onClick={() => setShowAllTools(v => !v)}
                  style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif', fontSize: 11, color: '#d4a418', opacity: 0.75, padding: '6px 0', textAlign: 'center' }}
                  data-testid="tools-show-all-btn"
                >
                  {showAllTools ? 'Show less ↑' : 'Show all tools →'}
                </button>
              </div>
            </div>
          </div>

          {/* CENTER */}
          <div className="jp-bento-col jp-bento-col-center">

            {dashboard?.showMuhurat && (
              <div className="hidden md:block">
                <button
                  onClick={() => setShowMuhuratModal(true)}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  data-testid="muhurat-trigger-btn"
                >
                <div className="jp-card" style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: muhuratData?.current ? 7 : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Clock style={{ width: 10, height: 10, color: 'var(--gold-primary)' }} />
                      <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 600, color: 'rgba(255,255,255,0.60)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                        CURRENT MUHURAT
                      </span>
                    </div>
                    <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'var(--gold-primary)' }}>
                      View Schedule →
                    </span>
                  </div>
                  {muhuratData?.current ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(() => {
                        const c = muhuratData.current;
                        const n = (c.name || '').toLowerCase();
                        const color = n === 'amrit' ? 'var(--gold-primary)' : n === 'shubh' ? 'rgba(112,224,160,0.9)' : n === 'labh' ? 'rgba(112,180,224,0.9)' : n === 'char' ? 'rgba(180,112,224,0.9)' : 'rgba(224,112,112,0.9)';
                        return (
                          <>
                            <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ fontFamily: 'Cinzel, serif', fontSize: 12, color, fontWeight: 500 }}>{c.name}</span>
                            <span style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'rgba(255,255,255,0.60)' }}>
                              {c.ruling_planet}{(c.start_time || c.end_time) ? ` · ${c.start_time || ''} – ${c.end_time || ''}` : ''}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'rgba(255,255,255,0.60)' }}>
                      {loading ? 'Loading…' : 'No muhurat data'}
                    </p>
                  )}
                </div>
              </button>
              </div>
            )}

            {dashboard?.showExploreDestiny && (
              <div>
                <h3 style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.60)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, paddingLeft: 2 }}>
                  EXPLORE YOUR DESTINY
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {services.map((service, index) => (
                    <motion.div
                      key={service.id}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.04 }}
                      data-testid={`service-card-${service.id}`}
                      className={`${service.isVIP ? 'col-span-2' : ''} ${service.id === 'chart' ? 'last-of-type:col-span-2 sm:last-of-type:col-span-1' : ''}`}
                    >
                      <PremiumCard
                        interactive onClick={service.action} glow={index === 0}
                        variant={service.isPremium ? 'premium' : 'glass'}
                        className={`group relative overflow-hidden h-full ${service.isPremium ? 'ring-1 ring-amber-500/30' : ''} ${service.isVIP ? 'ring-2 ring-amber-400/50 shadow-lg shadow-amber-500/20' : ''}`}
                      >
                        {service.badge && (
                          <div className={`absolute top-2 right-2 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                            service.badge === 'FREE' ? 'bg-emerald-500' :
                            service.badge === 'PREMIUM' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                            service.badge === 'UPGRADE' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                            service.badge === 'ACTIVE' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                            service.badge.includes('MIN') ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
                            'bg-red-500'
                          }`}>
                            {service.badgePulse && (
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-200 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-100"></span>
                              </span>
                            )}
                            {service.badge}
                          </div>
                        )}
                        <div className="space-y-2">
                          <div className={`w-9 h-9 rounded-xl bg-gradient-to-r ${service.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                            <service.icon className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white text-xs lg:text-sm">{service.title}</h4>
                            <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.60)' }}>{service.subtitle}</p>
                            <p className="text-[10px] leading-relaxed mt-1 line-clamp-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{service.description}</p>
                          </div>
                          {service.isExpandable && expandedCard === service.id && service.expandedContent}
                        </div>
                      </PremiumCard>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: ChitraGupt CTA + Karma + More */}
          <div className="jp-bento-col jp-bento-col-right">
            {!isAstrologerMode && (
              <div className="hidden md:block">
                <ProfileSwitcher
                  userInfo={userInfo}
                  familyProfiles={familyProfiles}
                  activeProfile={activeFamilyProfile}
                  onSwitch={(profile) => {
                    if (profile === null) { onSwitchToSelf?.(); }
                    else { onSwitchToFamilyProfile?.(profile); }
                  }}
                  onAdd={(newProfile) => {
                    onAddFamilyProfile?.(newProfile);
                    onSwitchToFamilyProfile?.(newProfile, { stayOnHome: true });
                  }}
                  onUpdate={(updated) => { onUpdateFamilyProfile?.(updated); }}
                  onDelete={(profileId) => { onDeleteFamilyProfile?.(profileId); }}
                  isLocked={false}
                />
              </div>
            )}
            <div className="hidden md:block">
              <ChitraguptCTACard
                horaData={horaData}
                onStartSession={handleAskChitragupt}
                onUpgrade={handleGuidanceClick}
                isAuthenticated={isAuthenticated}
                hasBalance={hasWalletBalance}
                resumeMode
              />
            </div>
            <div className="hidden md:block">
              <KarmaPulseCard
                karmaBalance={karmaBalance} walletMinutes={walletMinutes}
                loading={walletLoading} onTopUp={() => setShowCosmicStore(true)}
              />
            </div>
            {/* "More Tools & Help" card moved to Profile section */}
          </div>

        </div>

        {showMuhuratModal && (
          <React.Suspense fallback={<AppShellFallback />}>
            <MuhuratModal muhuratData={muhuratData} onClose={() => setShowMuhuratModal(false)} />
          </React.Suspense>
        )}

      </div>

      {showDownloadModal && (
        <React.Suspense fallback={<AppShellFallback />}>
          <DownloadButton
            userInfo={effectiveUserInfo} kundliData={effectiveKundliData}
            predictions={null} compatibilityData={null} autoOpen={true}
            onClose={() => setShowDownloadModal(false)}
            onShowSubscription={(focusPackId) => {
              setShowDownloadModal(false);
              setStoreFocusPackId(focusPackId);
              setShowCosmicStore(true);
            }}
            buttonText="Download Reports"
          />
        </React.Suspense>
      )}

      <AnimatePresence>
        {showAuthModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowAuthModal(false)} data-testid="auth-required-modal-overlay"
          >
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-gray-900 to-purple-900 rounded-2xl p-6 max-w-sm w-full border border-purple-500/30 shadow-xl z-[9999]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Sign in to access {authFeatureName}</h3>
                <p style={{ color: 'rgba(255,255,255,0.60)' }} className="text-sm mb-6">Create a free account to unlock personalized predictions and reports</p>
                <button
                  onClick={() => { if (startGoogleAuth) { startGoogleAuth(authFeatureName); } else { sessionStorage.setItem('authReturnPath', window.location.pathname); sessionStorage.setItem('authReturnView', 'dashboard'); sessionStorage.setItem('authFeature', authFeatureName || ''); window.location.href = '/login'; } }}
                  className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-3 transition-all"
                  data-testid="auth-modal-signin-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                <p style={{ color: 'rgba(255,255,255,0.40)' }} className="text-xs mt-4">Free forever • Secure • Takes 10 seconds</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSessionAuthWall && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowSessionAuthWall(false)} data-testid="session-auth-wall-overlay"
          >
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 rounded-2xl p-6 max-w-sm w-full border border-purple-500/30 shadow-2xl z-[9999]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30 relative">
                  <Timer className="w-10 h-10 text-white" />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                    <Lock className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Sign in to Start Session</h3>
                <p style={{ color: 'rgba(255,255,255,0.60)' }} className="text-sm mb-4">Create a free account to access premium guidance sessions with ChitraGupt.</p>
                <div className="bg-white/5 rounded-xl p-3 mb-5 text-left">
                  <p className="text-purple-300 text-xs font-medium mb-2">Why sign in?</p>
                  <ul className="space-y-1.5">
                    {['Secure payment linked to your account', 'Session history saved automatically', 'Wallet balance accessible anytime'].map((t, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.70)' }}>
                        <span className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">✓</span>{t}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => { setShowSessionAuthWall(false); if (startGoogleAuth) { startGoogleAuth('ChitraGupt Session'); } else { sessionStorage.setItem('authReturnPath', window.location.pathname); sessionStorage.setItem('authReturnView', 'dashboard'); sessionStorage.setItem('authFeature', 'ChitraGupt Session'); window.location.href = '/login'; } }}
                  className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-3 transition-all mb-3"
                  data-testid="session-auth-wall-signin-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                <button onClick={() => setShowSessionAuthWall(false)} style={{ color: 'rgba(255,255,255,0.50)' }} className="hover:text-white/70 text-sm transition-colors">Maybe later</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <React.Suspense fallback={<AppShellFallback />}>
        {showSessionMode && (
          <SessionModeController
            isOpen={showSessionMode}
            onClose={() => setShowSessionMode(false)}
            userInfo={effectiveUserInfo || userInfo}
            subject={buildProfileSubject(activeFamilyProfile)}
          />
        )}

        {showGuidanceModal && (
          <ChitraguptGuidanceModal
            isOpen={showGuidanceModal}
            onClose={() => { setShowGuidanceModal(false); }}
            onSelectPlan={(sessionData) => { console.log('[DASHBOARD] Plan selected:', sessionData); }}
            onPaymentSuccess={async (result) => {
              try {
                const newBalance = await handleWalletRefresh();
                setShowGuidanceModal(false);
                setTimeout(() => { setShowSessionMode(true); }, 100);
              } catch (err) {
                setShowGuidanceModal(false);
                setShowSessionMode(true);
              }
            }}
            refreshWallet={handleWalletRefresh}
            userInfo={userInfo}
            birthDetails={{ date: userInfo?.date, time: userInfo?.time, place: userInfo?.place, latitude: userInfo?.latitude, longitude: userInfo?.longitude }}
          />
        )}

        {showCosmicStore && (
          <CosmicStore
            userId={userInfo?.user_id}
            onClose={() => { setShowCosmicStore(false); setStoreFocusPackId(null); }}
            onPurchaseComplete={() => { fetchWalletBalance(); }}
            focusPackId={storeFocusPackId}
          />
        )}

        {showSubscriptionManager && (
          <SubscriptionManager userId={userInfo?.user_id} onClose={() => setShowSubscriptionManager(false)} onUpdate={() => { fetchWalletBalance(); }} />
        )}
      </React.Suspense>

      <AnimatePresence>
        {showHoraSchedule && horaData && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowHoraSchedule(false)}
          >
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-gradient-to-br from-slate-900 via-indigo-900/50 to-slate-900 rounded-2xl p-5 max-w-md w-full max-h-[80vh] overflow-y-auto border border-indigo-500/30 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Today's Hora Schedule</h3>
                <button onClick={() => setShowHoraSchedule(false)} style={{ color: 'rgba(255,255,255,0.60)' }} className="hover:text-white">✕</button>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.60)' }} className="text-xs mb-3">{horaData.day_name} • Sunrise: {horaData.sunrise} • Sunset: {horaData.sunset}</p>
              <div className="space-y-2">
                {horaData.hora_schedule?.map((hora, index) => (
                  <div key={index} className={`flex items-center justify-between p-2.5 rounded-xl transition-all ${hora.is_current ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40' : 'bg-white/5 border border-transparent'}`}>
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{hora.symbol}</span>
                      <div>
                        <p className={`font-medium text-sm ${hora.is_current ? 'text-amber-300' : 'text-white'}`}>{hora.planet} Hora</p>
                        <p style={{ color: 'rgba(255,255,255,0.45)' }} className="text-xs">{hora.start_time} - {hora.end_time}</p>
                      </div>
                    </div>
                    <div className={`px-2 py-0.5 rounded-full text-[10px] ${hora.quality === 'benefic' || hora.quality === 'royal' ? 'bg-emerald-500/20 text-emerald-300' : hora.quality === 'malefic' ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'}`}>
                      {hora.quality === 'benefic' || hora.quality === 'royal' ? 'Good' : hora.quality === 'malefic' ? 'Caution' : 'Neutral'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-white/5 rounded-xl">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.60)' }}><strong className="text-white">Hora Tip:</strong> Sun Hora for authority, Moon for travel, Mars for competitions, Mercury for communication, Jupiter for learning, Venus for relationships, Saturn for discipline.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-4 pb-4 hidden md:block">
        <LegalFooter />
      </div>

      <React.Suspense fallback={<AppShellFallback />}>
        {showNumerology && (
          <NumerologyModal
            isOpen={showNumerology}
            onClose={() => setShowNumerology(false)}
            userInfo={effectiveUserInfo || userInfo}
          />
        )}
        {showPanchang && (
          <PanchangScheduleModal
            isOpen={showPanchang}
            onClose={() => setShowPanchang(false)}
            latitude={userInfo?.latitude || 23.2599}
            longitude={userInfo?.longitude || 77.4126}
          />
        )}
        {showToolsMore && (
          <ToolsMoreModal
            isOpen={showToolsMore}
            onClose={() => setShowToolsMore(false)}
            userInfo={effectiveUserInfo || userInfo}
            onNavigate={onNavigate}
          />
        )}
      </React.Suspense>
      <AddFamilyMemberModal
        isOpen={showAddFamilyModal}
        onClose={() => setShowAddFamilyModal(false)}
        onAdd={(newProfile) => {
          onAddFamilyProfile?.(newProfile);
          onSwitchToFamilyProfile?.(newProfile, { stayOnHome: true });
          setShowAddFamilyModal(false);
        }}
      />
    </div>
  );
};

export default PremiumDashboard;
